import { useCallback, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { message, Modal } from "antd";
import dayjs from "dayjs";
import { generateRandomString } from "utils/helper";
import * as services from "../services/employeeApi";
import { setIsLoading, setWorkExperienceList } from "../slices/employeeSlice";
import { useTranslation } from "react-i18next";

function useEmployeeWorkExperience() {
  const dispatch = useDispatch();
  const params = useParams();
  const [isModalOpen, setModalOpen] = useState(false);
  const [dataDetail, setDataDetail] = useState(undefined);
  let { id } = useParams();
  let employeeId = id;
  const isLoading = useSelector((state) => state.employee.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const workExperienceList = useSelector(
    (state) => state.employee.workExperienceList
  );

  const [isEditing, setIsEditing] = useState(false);
  const { t, i18n } = useTranslation();
  const toParams = (values = {}) => {
    const modifiedValues = {
      ...values,
    };

    return modifiedValues;
  };

  const toInitialValues = (initialValues = {}) => {
    let startDate = initialValues?.startDate
      ? dayjs(initialValues?.startDate)
      : null;
    let endDate = initialValues?.endDate
      ? dayjs(initialValues?.endDate)
      : null;
    return {
      ...initialValues,
      company: initialValues?.company,
      position: initialValues?.position,
      latestSalary: initialValues?.latestSalary,
      startDate: startDate,
      endDate: endDate,
      responsibility: initialValues?.responsibility,
    };
  };

  const initialValues = useMemo(() => {
    return toInitialValues(dataDetail);
  }, [dataDetail]);

  const onEdit = (values) => {
    // setIsEditing(true);
    setModalOpen(true);
    setDataDetail(values);
  };

  const onCreate = useCallback(() => {
    // setCurrentYearAccumulate({});
    setModalOpen(true);
    setDataDetail(undefined);
  }, []);

  const onCancel = useCallback(() => {
    // setCurrentYearAccumulate({});
    setModalOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values) => {
      Modal.confirm({
        title: t('dialog.confirmation.header'),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to {values?.id ? "edit" : "create"} ?</span>
          : <span>คุณแน่ใจหรือไม่ที่จะ {values?.id ? "แก้ไข" : "สร้างใหม่"} ?</span>
        ),
        onOk() {
          updateWorkExperienceInformation(values);
        },
        okText: t('confirm'),
        cancelText: t('generalInfo.cancel'),
      });
    },
    []
  );

  const updateWorkExperienceInformation = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.updateWorkExperienceInformation(
          toParams({
            ...values,
            requestId: generateRandomString(),
            companyId: currentCompany,
            employeeId: employeeId,
            workExperienceId: values?.id,
            startDate: values?.startDate ? dayjs(values?.startDate).format("YYYY-MM-DD") : null,
            endDate: values?.endDate ? dayjs(values?.endDate).format("YYYY-MM-DD") : null,
          })
        );
        message.success(t("common.success"))
        setModalOpen(false);
        setIsEditing(false);
        getWorkExperienceInformation(
          id
        );
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const onDelete = useCallback(
    async (params = {}) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>"Are you sure confirm to delete ?</span>
          : <span>คุณยืนยันการลบใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            const response = await services.deleteWorkExperienceInformation({
              employeeId: id,
              actionId: currentEmployee,
              workExperienceId: params?.id,

            });
            getWorkExperienceInformation(
              id
            );
            setWorkExperienceList({});
            setModalOpen(false);
            message.success(t("common.success"))
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })
    },
    [dispatch, id, params]
  );

  const getWorkExperienceInformation = useCallback(
    async (employeeId) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getWorkExperienceInformation({
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId,
        });
        dispatch(
          setWorkExperienceList((response.data || {}).workExperience || [])
        );
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  useEffect(() => {
    if (params.id && currentCompany) getWorkExperienceInformation(params.id);
  }, [currentCompany, params.id, getWorkExperienceInformation]);

  return {
    isLoading,
    dataDetail,
    isModalOpen,
    initialValues,
    workExperienceList,
    onEdit,
    onCreate,
    onCancel,
    onSubmit,
    onDelete,
    updateWorkExperienceInformation
  };
}

export default useEmployeeWorkExperience;
